import { Logo } from '@/components/ui/Logo'
import { useSite } from '@/app/site-context'
import { reviewNoteFor } from '@/i18n/review-notes'
import { siteConfig } from '@/lib/site-config'

interface FooterProps {
  onNavigate: (href: string) => void
}

export function Footer({ onNavigate }: FooterProps) {
  const { dictionary, pathFor, route, locale } = useSite()
  const reviewNote = reviewNoteFor(locale)
  const year = new Date().getFullYear()
  const home = pathFor('home')
  const isHome = route.kind === 'home'
  const sectionHref = (id: string) => (isHome ? `#${id}` : `${home}#${id}`)

  const navigate = (event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('#')) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
    event.preventDefault()
    onNavigate(href)
  }

  return (
    <footer className="footer">
      <div className="footer__grid">
        <div className="footer__brand">
          <a
            className="footer__logo"
            href={home}
            onClick={(event) => {
              navigate(event, home)
            }}
          >
            <Logo size={28} />
            <span>
              DALMOS<em>SYSTEMS</em>
            </span>
          </a>
          <p className="footer__tagline">{dictionary.footer.tagline}</p>
        </div>

        <nav className="footer__column" aria-label={dictionary.footer.navTitle}>
          <h2 className="footer__heading">{dictionary.footer.navTitle}</h2>
          <a
            href={sectionHref('services')}
            onClick={(event) => {
              navigate(event, sectionHref('services'))
            }}
          >
            {dictionary.nav.services}
          </a>
          <a
            href={sectionHref('about')}
            onClick={(event) => {
              navigate(event, sectionHref('about'))
            }}
          >
            {dictionary.nav.about}
          </a>
          <a
            href={sectionHref('contact')}
            onClick={(event) => {
              navigate(event, sectionHref('contact'))
            }}
          >
            {dictionary.nav.contact}
          </a>
        </nav>

        <div className="footer__column">
          <h2 className="footer__heading">{dictionary.nav.contact}</h2>
          <a href={`mailto:${siteConfig.salesEmail}`}>{siteConfig.salesEmail}</a>
          <a href={`tel:${siteConfig.phone.replace(/\s/g, '')}`}>{siteConfig.phone}</a>
          <span className="footer__muted">{dictionary.footer.builtIn}</span>
        </div>

        <nav className="footer__column" aria-label={dictionary.footer.legalTitle}>
          <h2 className="footer__heading">{dictionary.footer.legalTitle}</h2>
          <a
            href={pathFor('legal')}
            onClick={(event) => {
              navigate(event, pathFor('legal'))
            }}
          >
            {dictionary.footer.legalNotice}
          </a>
          <a
            href={pathFor('privacy')}
            onClick={(event) => {
              navigate(event, pathFor('privacy'))
            }}
          >
            {dictionary.footer.privacy}
          </a>
        </nav>
      </div>

      <div className="footer__base">
        <p className="footer__legal">
          {siteConfig.legalName} · MBO {siteConfig.register.mbo} ·{' '}
          <a href={`mailto:${siteConfig.email}`}>{siteConfig.email}</a>
        </p>
        <p>
          © {year} {siteConfig.shortLegalName}. {dictionary.footer.rights}
        </p>
        {reviewNote === undefined ? null : <p className="footer__review">{reviewNote.site}</p>}
      </div>
    </footer>
  )
}
